/**
 * FlowNotes API Client - Ingestion
 * Basecamp todo and message ingestion functions
 */

import { fetchWithAuth, API_BASE_URL } from './core';
import { toast } from 'sonner';

export interface BasecampIngestionResult {
  success: boolean;
  conversationId?: string;
  threadId?: string;
  status?: 'duplicate_detected' | 'success' | 'error' | 'processing' | 'agent_session';
  mode?: 'agent' | 'legacy';
  documentId?: string;
  todoId?: string;
  messageId?: string;
  todoFile?: string;
  messageFile?: string;
  clientFile?: string;
  title?: string;
  project?: string;
  action?: 'created' | 'updated';
  error?: string;
  /** Present when UPDATE_WORK_SPEC_MODE blocks or warns on update path */
  code?: string;
  updateWorkSpecMode?: 'warn' | 'block' | 'legacy';
  updateWorkSpecWarning?: string;
}

/** Thrown when ingest-todo update is rejected because UPDATE_WORK_SPEC_MODE=block. */
export class UpdateWorkSpecBlockedError extends Error {
  code = 'UPDATE_WORK_SPEC_BLOCKED';
  updateWorkSpecMode: 'block';

  constructor(message: string) {
    super(message);
    this.name = 'UpdateWorkSpecBlockedError';
    this.updateWorkSpecMode = 'block';
  }
}

export interface BasecampEnabledWorkspace {
  id: string;
  name: string;
}

/** Thrown when the API returns 403 basecamp_not_enabled_for_workspace for the requested workspace. */
export class BasecampWorkspaceBlockedError extends Error {
  code = 'basecamp_not_enabled_for_workspace';
  organizationId: string | null;
  enabledWorkspaces: BasecampEnabledWorkspace[];

  constructor(body: { error?: string; organizationId?: string | null; enabledWorkspaces?: BasecampEnabledWorkspace[] }) {
    super(body.error || 'Basecamp is not enabled in this workspace');
    this.name = 'BasecampWorkspaceBlockedError';
    this.organizationId = body.organizationId ?? null;
    this.enabledWorkspaces = body.enabledWorkspaces ?? [];
  }
}

function throwIfBasecampWorkspaceBlocked(
  status: number,
  body: { error_code?: string; error?: string; organizationId?: string | null; enabledWorkspaces?: BasecampEnabledWorkspace[] },
): void {
  if (status === 403 && body?.error_code === 'basecamp_not_enabled_for_workspace') {
    throw new BasecampWorkspaceBlockedError(body);
  }
}

/**
 * Returns true when error was a Basecamp workspace block (toast shown).
 * `onUseWorkspace` adds the workspace to scope and retries; omitted = message only.
 */
export function handleBasecampWorkspaceBlockedError(
  error: unknown,
  onUseWorkspace?: (workspace: BasecampEnabledWorkspace) => void,
): boolean {
  if (!(error instanceof BasecampWorkspaceBlockedError)) return false;
  const target = error.enabledWorkspaces[0];
  toast.error('Basecamp is not enabled in this workspace', {
    id: 'basecamp-workspace-blocked',
    description: target
      ? `Basecamp is enabled in ${target.name}. Add it to your workspace scope to ingest there.`
      : 'Enable Basecamp for a workspace in Settings > Extensions first.',
    duration: 10000,
    ...(target && onUseWorkspace
      ? { action: { label: `Use ${target.name}`, onClick: () => onUseWorkspace(target) } }
      : {}),
  });
  return true;
}

/**
 * Ingest a Basecamp todo (fetch JSON + AI conversion + file creation)
 */
export async function ingestBasecampTodo(
  url: string,
  options: { forceRefresh?: boolean; organizationId?: string; mode?: 'agent' | 'legacy' } = {}
): Promise<BasecampIngestionResult> {
  const { organizationId, mode, ...rest } = options;
  const response = await fetchWithAuth(`${API_BASE_URL}/api/basecamp/ingest-todo`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      url,
      ...rest,
      ...(mode ? { mode } : {}),
      ...(organizationId ? { organizationId } : {}),
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throwIfBasecampWorkspaceBlocked(response.status, error);
    if (response.status === 503 && error.code === 'UPDATE_WORK_SPEC_BLOCKED') {
      throw new UpdateWorkSpecBlockedError(
        error.error || 'Update Work Spec is temporarily disabled.'
      );
    }
    const errorWithConversation: any = new Error(error.error || 'Failed to ingest Basecamp todo');
    errorWithConversation.conversationId = error.conversationId;
    errorWithConversation.threadId = error.threadId;

    if (response.status === 401 && error.auth_url) {
      errorWithConversation.status = 401;
      errorWithConversation.auth_url = error.auth_url;
    }

    throw errorWithConversation;
  }

  return response.json();
}

/** Agent harness Update Work Spec session (Wave B) - not scripted ingest polling. */
export function isAgentUpdateWorkSpecResult(result: BasecampIngestionResult): boolean {
  return result.status === 'agent_session' || result.mode === 'agent';
}

/** Show warn-mode notice when API returns updateWorkSpecWarning on legacy update path. */
export function applyUpdateWorkSpecModeUi(result: BasecampIngestionResult): void {
  if (isAgentUpdateWorkSpecResult(result)) return;
  if (result.updateWorkSpecWarning) {
    toast.warning('Update Work Spec safety notice', {
      description: result.updateWorkSpecWarning,
      duration: 8000,
    });
  }
}

/** Returns true when error was handled (blocked mode). */
export function handleUpdateWorkSpecBlockedError(error: unknown): boolean {
  if (error instanceof UpdateWorkSpecBlockedError) {
    toast.error('Update Work Spec temporarily disabled', {
      description: error.message,
      duration: 10000,
    });
    return true;
  }
  return false;
}

/**
 * Bulk ingest all todos in a Basecamp todolist (including nested sections).
 */
export async function ingestBasecampTodolist(
  url: string,
  options: { forceRefresh?: boolean; skipExisting?: boolean; organizationId?: string } = {}
): Promise<BasecampIngestionResult> {
  const { organizationId, ...rest } = options;
  const response = await fetchWithAuth(`${API_BASE_URL}/api/basecamp/ingest-todolist`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      url,
      skipExisting: true,
      ...rest,
      ...(organizationId ? { organizationId } : {}),
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throwIfBasecampWorkspaceBlocked(response.status, error);
    const errorWithConversation: any = new Error(error.error || 'Failed to bulk ingest Basecamp todolist');
    errorWithConversation.conversationId = error.conversationId;
    errorWithConversation.threadId = error.threadId;
    throw errorWithConversation;
  }

  return response.json();
}

/**
 * Test custom prompt for ingestion
 */
export async function testIngestionPrompt(options: {
  todoId: string;
  projectId: string;
  prompt: string;
  model?: string;
  temperature?: number;
  provider?: string;
}): Promise<{
  success: boolean;
  markdown: string;
  frontmatter: Record<string, any>;
  qualityScore: number;
  qualityBreakdown: Record<string, number>;
  suggestions: string[];
  feedback: string[];
}> {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/ai/ingestion/test-prompt`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify(options)
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to test prompt');
  }

  return response.json();
}

/**
 * Run A/B test with two prompt variations
 */
export async function runABTest(options: {
  todoId: string;
  projectId: string;
  promptA: string;
  promptB: string;
}): Promise<{
  success: boolean;
  optionA: {
    markdown: string;
    frontmatter: Record<string, any>;
    quality: {
      score: number;
      breakdown: Record<string, number>;
      feedback: string[];
      suggestions: string[];
    };
    prompt: string;
  };
  optionB: {
    markdown: string;
    frontmatter: Record<string, any>;
    quality: {
      score: number;
      breakdown: Record<string, number>;
      feedback: string[];
      suggestions: string[];
    };
    prompt: string;
  };
}> {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/ai/ingestion/ab-test`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify(options)
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to run A/B test');
  }

  return response.json();
}

/**
 * Ingest a Basecamp message or inbox forward (fetch JSON + AI conversion + file creation)
 */
export async function ingestBasecampMessage(
  url: string,
  options: { forceRefresh?: boolean; organizationId?: string } = {}
): Promise<BasecampIngestionResult> {
  const { organizationId, ...rest } = options;
  const response = await fetchWithAuth(`${API_BASE_URL}/api/basecamp/ingest-message`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      url,
      ...rest,
      ...(organizationId ? { organizationId } : {}),
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throwIfBasecampWorkspaceBlocked(response.status, error);
    const errorWithConversation: any = new Error(error.error || 'Failed to ingest Basecamp message');
    errorWithConversation.conversationId = error.conversationId;
    errorWithConversation.threadId = error.threadId;

    if (response.status === 401 && error.auth_url) {
      errorWithConversation.status = 401;
      errorWithConversation.auth_url = error.auth_url;
    }

    throw errorWithConversation;
  }

  return response.json();
}
